import { Badge } from "frontend-kit/components/index.js";
import { renderTxCell } from "./tx-cell.js";

const BADGE = {
  submitted: ["pending", "Submitted"],
  executing: ["pending", "Confirming…"],
  retrying: ["warning", "Retrying…"],
  executed: ["success", "Executed"],
  failed: ["danger", "Failed"],
  timeout: ["warning", "Timed out"],
};

let globalHost = null;
let dismissTimer = null;
let generation = 0;

/** Bind the sticky global banner element (once, from main.js). */
export function initGlobalTxBanner(el) {
  globalHost = el;
  if (globalHost) {
    globalHost.hidden = true;
    globalHost.setAttribute("role", "status");
    globalHost.setAttribute("aria-live", "polite");
  }
}

/**
 * Show / update the global header banner.
 * @param {{ label?: string, hash?: string }} opts
 */
export function showGlobalTx({ label = "Transaction", hash = "" } = {}) {
  if (!globalHost) {
    throw new Error("Global tx banner not initialized");
  }
  if (dismissTimer) {
    clearTimeout(dismissTimer);
    dismissTimer = null;
  }
  generation += 1;
  const gen = generation;

  globalHost.hidden = false;
  globalHost.replaceChildren();
  globalHost.className = "global-tx-banner is-active dt-card";
  globalHost.dataset.status = "submitted";

  const inner = document.createElement("div");
  inner.className = "global-tx-inner";

  const left = document.createElement("div");
  left.className = "global-tx-left";
  const title = document.createElement("strong");
  title.className = "global-tx-label";
  title.append(label);
  left.append(title);

  const badgeHost = document.createElement("div");
  badgeHost.className = "global-tx-badge";
  left.append(badgeHost);

  const meta = document.createElement("div");
  meta.className = "global-tx-meta";
  const hashEl = document.createElement("div");
  hashEl.className = "global-tx-hash";
  if (hash) {
    hashEl.replaceChildren(renderTxCell(hash, { keep: 10 }));
  } else {
    hashEl.textContent = "Waiting for wallet…";
  }
  const detail = document.createElement("span");
  detail.className = "global-tx-detail";
  detail.textContent = "Approve in your wallet if prompted.";
  meta.append(hashEl, detail);

  const dismiss = document.createElement("button");
  dismiss.type = "button";
  dismiss.className = "global-tx-dismiss";
  dismiss.setAttribute("aria-label", "Dismiss");
  dismiss.textContent = "×";
  dismiss.addEventListener("click", () => hideGlobalTx());

  inner.append(left, meta, dismiss);
  globalHost.append(inner);

  function setStatus(status, message = "") {
    if (gen !== generation) return;
    globalHost.dataset.status = status;
    const [variant, text] = BADGE[status] ?? ["neutral", status];
    badgeHost.replaceChildren(Badge({ variant, label: text }));
    if (message) detail.textContent = message;
    globalHost.classList.toggle("is-success", status === "executed");
    globalHost.classList.toggle("is-error", status === "failed" || status === "timeout");
  }

  setStatus("submitted", "Approve in your wallet if prompted.");

  return {
    setHash(h) {
      if (gen !== generation) return;
      hash = h || hash;
      if (hash) {
        hashEl.replaceChildren(renderTxCell(hash, { keep: 10 }));
      }
    },
    setStatus,
    setDetail(message) {
      if (gen !== generation) return;
      detail.textContent = message;
    },
    dismissLater(ms = 6000) {
      if (gen !== generation) return;
      if (dismissTimer) clearTimeout(dismissTimer);
      dismissTimer = setTimeout(() => {
        if (gen === generation) hideGlobalTx();
      }, ms);
    },
  };
}

export function hideGlobalTx() {
  if (dismissTimer) {
    clearTimeout(dismissTimer);
    dismissTimer = null;
  }
  generation += 1;
  if (globalHost) {
    globalHost.hidden = true;
    globalHost.replaceChildren();
    globalHost.className = "global-tx-banner";
    delete globalHost.dataset.status;
  }
}

/**
 * Mount a local (inline) tx status strip.
 * @param {HTMLElement} host
 * @param {{ hash?: string, label?: string }} opts
 */
export function mountTxStatus(host, { hash = "", label = "Transaction" } = {}) {
  host.replaceChildren();
  host.hidden = false;
  host.className = "tx-status dt-card";

  const head = document.createElement("div");
  head.className = "tx-status-head";
  const title = document.createElement("strong");
  title.append(label);
  head.append(title);
  host.append(head);

  const badgeHost = document.createElement("div");
  badgeHost.className = "tx-status-badge";
  head.append(badgeHost);

  const hashEl = document.createElement("p");
  hashEl.className = "tx-status-hash hint";
  hashEl.textContent = hash ? `tx ${shortHash(hash)}` : "Waiting for wallet…";
  host.append(hashEl);

  const detail = document.createElement("p");
  detail.className = "tx-status-detail hint";
  host.append(detail);

  function setStatus(status, message = "") {
    const [variant, text] = BADGE[status] ?? ["neutral", status];
    badgeHost.replaceChildren(Badge({ variant, label: text }));
    if (message) detail.textContent = message;
  }

  setStatus("submitted", "Approve in your wallet if prompted.");

  return {
    setHash(h) {
      hash = h || hash;
      hashEl.textContent = hash ? `tx ${shortHash(hash)}` : hashEl.textContent;
    },
    setStatus,
    setDetail(message) {
      detail.textContent = message;
    },
    el: host,
  };
}

/**
 * Track a @dusk/connect TxHandle through confirmation.
 * @param {object} tx
 * @param {{ ui?: object, timeoutMs?: number, autoDismissMs?: number|null }} opts
 */
export async function trackTx(tx, { ui, timeoutMs = 90_000, autoDismissMs = null } = {}) {
  const hash = String(tx?.hash || "").replace(/^0x/i, "");
  ui?.setHash(hash);

  if (typeof tx?.onStatus === "function") {
    tx.onStatus((update) => {
      ui?.setStatus(update.status);
      if (update.status === "failed" && update.receipt?.error) {
        ui?.setDetail(String(update.receipt.error));
      }
    });
  }

  if (typeof tx?.wait !== "function") {
    ui?.setStatus("submitted", "Submitted - confirmation tracking unavailable.");
    return { hash, status: "submitted", ok: null };
  }

  ui?.setStatus("executing", "Waiting for inclusion on testnet…");
  const receipt = await tx.wait({ timeoutMs });
  const status = receipt?.status ?? (receipt?.ok ? "executed" : "failed");
  ui?.setStatus(status, receipt?.error ? String(receipt.error) : statusMessage(status));
  if (status === "executed" && autoDismissMs != null && typeof ui?.dismissLater === "function") {
    ui.dismissLater(autoDismissMs);
  }
  if (receipt && receipt.ok === false) {
    throw new Error(receipt.error || `Transaction ${status}`);
  }
  return { hash, status, ok: receipt?.ok !== false, receipt };
}

/** Convenience: show global banner and track until confirmed. */
export async function trackGlobalTx(tx, { label, timeoutMs, autoDismissMs = 8000 } = {}) {
  const ui = showGlobalTx({ label, hash: tx?.hash });
  return trackTx(tx, { ui, timeoutMs, autoDismissMs });
}

function shortHash(hash) {
  const h = String(hash).replace(/^0x/i, "");
  if (h.length <= 16) return h;
  return `${h.slice(0, 10)}…${h.slice(-8)}`;
}

function statusMessage(status) {
  switch (status) {
    case "executed":
      return "Confirmed on-chain.";
    case "failed":
      return "On-chain execution failed.";
    case "timeout":
      return "Still pending - check again in a minute.";
    default:
      return "";
  }
}
